import { trigger } from '@angular/animations';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { MatButtonToggleChange } from '@angular/material/button-toggle';
import { differenceInDays } from 'date-fns';
import { BarSeriesOption, LabelFormatterCallback, DefaultLabelFormatterCallbackParams } from 'echarts';
import { CallbackDataParams } from 'echarts/types/dist/shared';

import { NSOrderFillConstants } from '@ruby/configs/order-fill.constants';
import { IOrderFillDateRange, IOrderFillDetail, IOrderFillViewDateRange } from '@ruby/modules/order-fill/model/order-fill.interface';
import { fadeIn } from '@ruby/shared/animations/util.animations';
import { EOrderBarChartTitle, EOrderFillDateInterval, EOrderViewType } from '@ruby/shared/enums/order-fill.enums';
import { NSDateUtils } from '@ruby/shared/services/utils/ns-date.utils';

@Component({
  selector: 'ng-weekly-chart',
  templateUrl: './weekly-chart.component.html',
  styleUrls: ['./weekly-chart.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('fadeIn', fadeIn())
  ]
})
export class WeeklyChartComponent implements OnChanges {
  @Input() title!: EOrderBarChartTitle;
  @Input() metrics: Array<IOrderFillDetail> = [];
  @Input() dateInterval!: { previous?: EOrderFillDateInterval; current?: EOrderFillDateInterval };
  @Input() doofFeature!: boolean;
  @Output() changeViewType: EventEmitter<IOrderFillViewDateRange> = new EventEmitter();
  config!: Record<string, unknown>;
  showPercentage = true;
  isEmptyBarChart = true;
  eOrderBarChartTitleType = EOrderBarChartTitle;

  constructor() {
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.metrics) {
      this.config = this.setUpBarChart();
    }
  }

  /**
   * Chart Clicked
   *
   * @summary Called when the chart is clicked
   * @param $target: unknown
   * @returns void
   */
  chartClicked($target: unknown): void {
    if (!this.doofFeature) {
      return;
    }

    const target: CallbackDataParams = $target as CallbackDataParams;
    const details: IOrderFillDetail = this.metrics[target.dataIndex];

    if (differenceInDays(details.endDate, details.startDate) <= 7) {
      const range: IOrderFillDateRange = {
        startDate: details.startDate,
        endDate: details.endDate
      };

      this.changeViewType.emit({
        range,
        viewType: EOrderViewType.DAILY,
        dateInterval: {
          previous: this.dateInterval.current!,
          current: EOrderFillDateInterval.CUSTOM
        }
      });
    }
  }

  /**
   * Toggle Percentage Format
   *
   * @summary Toggles the display of the values between percentage and counts
   * @param $change: MatButtonToggleChange
   * @returns void
   */
  togglePercentageFormat($change: MatButtonToggleChange): void {
    this.showPercentage = $change.value;
    this.config = this.setUpBarChart();
  }

  /**
   * Convert to Percentage
   *
   * @summary Converts a value to percentage with two decimal places
   * @param $value: number
   * @param $total: number
   * @returns number
   */
  convertToPercentage($value: number, $total: number): number {
    return $total > 0 ? Math.ceil(($value / $total) * 100) : 0;
  }

  private setUpBarChart(): Record<string, unknown> {
    const xAxisData: Array<string> = [];
    const requestedSeries: Array<number> = [];
    const filledBeforeSeries: Array<number> = this.metrics.map((detail: IOrderFillDetail) => detail.filledBeforeCutoff);
    const filledAfterSeries: Array<number> = this.metrics.map((detail: IOrderFillDetail) => detail.filledAfterCutoff);
    const acceptedNotFilledSeries: Array<number> = this.metrics.map((detail: IOrderFillDetail) => detail.acceptedNotFilled);
    const notAcceptedSeries: Array<number> = this.metrics.map((detail: IOrderFillDetail) => detail.notAccepted);

    this.metrics.forEach((detail: IOrderFillDetail) => {
      if (this.title === EOrderBarChartTitle.WEEKLY) {
        xAxisData.push(this.generateWeeklyLabel(detail.startDate, detail.endDate));
      } else if (this.title === EOrderBarChartTitle.MONTHLY) {
        xAxisData.push(this.generateMonthlyLabel(detail.startDate));
      }
      requestedSeries.push(detail.requested);
    });

    const maxValues: Array<number> = [];

    filledBeforeSeries.forEach((value: number, index: number) => {
      maxValues.push(value + filledAfterSeries[index] + acceptedNotFilledSeries[index] + notAcceptedSeries[index]);
    });

    const maxValue: number = Math.ceil(Math.max(...maxValues) / 10) * 10;
    const allSeriesArray = [filledBeforeSeries, filledAfterSeries, acceptedNotFilledSeries, notAcceptedSeries, requestedSeries];
    this.isEmptyBarChart = allSeriesArray.every((data: Array<number>) => data.length === 0) || allSeriesArray.every((data: Array<number>) => data.every((value) => value === 0));

    return {
      ...NSOrderFillConstants.weeklyLineConfig,
      silent: !this.doofFeature,
      xAxis: {
        ...NSOrderFillConstants.weeklyLineConfig.xAxis,
        data: xAxisData
      },
      yAxis: {
        ...NSOrderFillConstants.weeklyLineConfig.yAxis,
        max: maxValue
      },
      series: [
        {
          ...NSOrderFillConstants.weeklyLineConfig.series[0],
          label: {
            ...NSOrderFillConstants.weeklyLineConfig.series[0].label,
            formatter: this.updateBarChartFormatter(NSOrderFillConstants.weeklyLineConfig.series[0] as BarSeriesOption)
          },
          data: filledBeforeSeries
        },
        {
          ...NSOrderFillConstants.weeklyLineConfig.series[1],
          label: {
            ...NSOrderFillConstants.weeklyLineConfig.series[1].label,
            formatter: this.updateBarChartFormatter(NSOrderFillConstants.weeklyLineConfig.series[1] as BarSeriesOption)
          },
          data: filledAfterSeries
        },
        {
          ...NSOrderFillConstants.weeklyLineConfig.series[2],
          label: {
            ...NSOrderFillConstants.weeklyLineConfig.series[2].label,
            formatter: this.updateBarChartFormatter(NSOrderFillConstants.weeklyLineConfig.series[2] as BarSeriesOption)
          },
          data: acceptedNotFilledSeries
        },
        {
          ...NSOrderFillConstants.weeklyLineConfig.series[3],
          label: {
            ...NSOrderFillConstants.weeklyLineConfig.series[3].label,
            formatter: this.updateBarChartFormatter(NSOrderFillConstants.weeklyLineConfig.series[3] as BarSeriesOption)
          },
          data: notAcceptedSeries
        },
        {
          ...NSOrderFillConstants.weeklyLineConfig.series[4],
          data: requestedSeries
        }
      ]
    };
  }

  private updateBarChartFormatter(formatterConfig: BarSeriesOption): LabelFormatterCallback<DefaultLabelFormatterCallbackParams> {
    const formatterFn: LabelFormatterCallback<DefaultLabelFormatterCallbackParams> =
      formatterConfig.label!.formatter as LabelFormatterCallback<DefaultLabelFormatterCallbackParams>;

    if (this.showPercentage) {
      return (params: DefaultLabelFormatterCallbackParams) =>
        `${ formatterFn({
          ...params,
          value: this.convertToPercentage(params.value as number, this.metrics[params.dataIndex].requested)
        }) }${ Number(params.value) > 0 ? '%' : '' }`;
    }

    return formatterFn;
  }

  private generateWeeklyLabel(weekStart: number, weekEnd: number): string {
    return `${ NSDateUtils.formatNSDate(weekStart, 'M/d') } - ${ NSDateUtils.formatNSDate(weekEnd, 'M/d') }`;
  }

  private generateMonthlyLabel(monthStart: number): string {
    return NSDateUtils.formatNSDate(monthStart, 'MMM');
  }
}
